<template>
  <main class="admin-schedule-page">
    <header class="page-heading">
      <div>
        <span class="eyebrow">CONTROL OPERATIVO</span>
        <h1>Admin Schedule</h1>
        <p>Plan the weekly hours and activities for supervisors and Satellite operations.</p>
      </div>
      <div class="page-actions">
        <v-chip v-if="!loading && !canEdit" size="small" color="grey" variant="tonal">View Only</v-chip>
        <span v-if="canEdit" :class="['save-status', saveState]">{{ saveStatusText }}</span>
        <v-btn
          variant="outlined"
          prepend-icon="mdi-microsoft-excel"
          :disabled="loading || !people.length"
          @click="downloadExcel"
        >
          Download Excel
        </v-btn>
        <v-btn
          v-if="canEdit"
          :color="isEditing ? 'success' : 'primary'"
          :variant="isEditing ? 'flat' : 'outlined'"
          :prepend-icon="isEditing ? 'mdi-check' : 'mdi-pencil-outline'"
          :disabled="loading"
          @click="isEditing = !isEditing"
        >
          {{ isEditing ? 'Done Editing' : 'Edit Schedule' }}
        </v-btn>
      </div>
    </header>

    <v-card class="schedule-controls" elevation="0">
      <div></div>
      <div class="week-control">
        <v-btn icon="mdi-chevron-left" variant="text" aria-label="Previous week" @click="moveWeek(-1)" />
        <div class="week-title">
          <small>MONDAY — SUNDAY</small>
          <strong>{{ weekHeading }}</strong>
        </div>
        <v-btn icon="mdi-chevron-right" variant="text" aria-label="Next week" @click="moveWeek(1)" />
      </div>
      <div class="control-actions">
        <v-btn size="small" variant="text" @click="goToCurrentWeek">This Week</v-btn>
        <v-btn
          v-if="canEdit && isEditing"
          size="small"
          variant="text"
          prepend-icon="mdi-content-copy"
          @click="copyPreviousWeek"
        >
          Copy Previous Week
        </v-btn>
      </div>
    </v-card>

    <v-alert v-if="error" type="warning" variant="tonal" closable class="mb-4" @click:close="error = ''">
      {{ error }}
    </v-alert>

    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate color="primary" />
      <span>Loading Admin Schedule…</span>
    </div>

    <v-card v-else class="schedule-panel" elevation="0">
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th class="person-column">Team</th>
              <th v-for="day in weekDays" :key="day.date">
                <span>{{ day.name }}</span>
                <strong>{{ day.number }}</strong>
              </th>
              <th class="total-column">Hours</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="person in people" :key="person.key">
              <td class="person-cell">
                <img
                  v-if="profilePhoto(person.key)"
                  :src="profilePhoto(person.key)"
                  :alt="`${person.name} profile photo`"
                  class="person-avatar person-avatar-photo"
                />
                <div v-else class="person-avatar">{{ initials(person.name) }}</div>
                <strong>{{ person.name }}</strong>
                <small>{{ person.role }}</small>
              </td>
              <td v-for="day in person.days" :key="day.date" class="schedule-day-cell">
                <div v-if="isEditing" class="day-editor">
                  <div class="time-fields">
                    <v-menu v-model="day.start_picker_open" :close-on-content-click="false" location="bottom">
                      <template #activator="{ props }">
                        <v-text-field
                          v-bind="props"
                          :model-value="formatTime(day.start_time)"
                          label="Start"
                          density="compact"
                          variant="outlined"
                          hide-details
                          readonly
                          :disabled="day.is_day_off"
                        />
                      </template>
                      <v-card class="time-picker-card" elevation="8">
                        <v-time-picker
                          v-model="day.start_time"
                          format="ampm"
                          ampm-in-title
                          color="primary"
                          @update:model-value="queueAutoSave()"
                        />
                        <v-card-actions>
                          <v-btn size="small" variant="text" @click="clearTime(day, 'start_time', 'start_picker_open')">Clear</v-btn>
                          <v-spacer />
                          <v-btn size="small" color="primary" variant="text" @click="day.start_picker_open = false">Done</v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-menu>
                    <v-menu v-model="day.end_picker_open" :close-on-content-click="false" location="bottom">
                      <template #activator="{ props }">
                        <v-text-field
                          v-bind="props"
                          :model-value="formatTime(day.end_time)"
                          label="End"
                          density="compact"
                          variant="outlined"
                          hide-details
                          readonly
                          :disabled="day.is_day_off"
                        />
                      </template>
                      <v-card class="time-picker-card" elevation="8">
                        <v-time-picker
                          v-model="day.end_time"
                          format="ampm"
                          ampm-in-title
                          color="primary"
                          @update:model-value="queueAutoSave()"
                        />
                        <v-card-actions>
                          <v-btn size="small" variant="text" @click="clearTime(day, 'end_time', 'end_picker_open')">Clear</v-btn>
                          <v-spacer />
                          <v-btn size="small" color="primary" variant="text" @click="day.end_picker_open = false">Done</v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-menu>
                  </div>
                  <div v-if="day.is_split_shift && !day.is_day_off" class="time-fields second-time-fields">
                    <v-menu v-model="day.second_start_picker_open" :close-on-content-click="false" location="bottom">
                      <template #activator="{ props }">
                        <v-text-field
                          v-bind="props"
                          :model-value="formatTime(day.second_start_time)"
                          label="Start 2"
                          density="compact"
                          variant="outlined"
                          hide-details
                          readonly
                        />
                      </template>
                      <v-card class="time-picker-card" elevation="8">
                        <v-time-picker
                          v-model="day.second_start_time"
                          format="ampm"
                          ampm-in-title
                          color="primary"
                          @update:model-value="queueAutoSave()"
                        />
                        <v-card-actions>
                          <v-btn size="small" variant="text" @click="clearTime(day, 'second_start_time', 'second_start_picker_open')">Clear</v-btn>
                          <v-spacer />
                          <v-btn size="small" color="primary" variant="text" @click="day.second_start_picker_open = false">Done</v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-menu>
                    <v-menu v-model="day.second_end_picker_open" :close-on-content-click="false" location="bottom">
                      <template #activator="{ props }">
                        <v-text-field
                          v-bind="props"
                          :model-value="formatTime(day.second_end_time)"
                          label="End 2"
                          density="compact"
                          variant="outlined"
                          hide-details
                          readonly
                        />
                      </template>
                      <v-card class="time-picker-card" elevation="8">
                        <v-time-picker
                          v-model="day.second_end_time"
                          format="ampm"
                          ampm-in-title
                          color="primary"
                          @update:model-value="queueAutoSave()"
                        />
                        <v-card-actions>
                          <v-btn size="small" variant="text" @click="clearTime(day, 'second_end_time', 'second_end_picker_open')">Clear</v-btn>
                          <v-spacer />
                          <v-btn size="small" color="primary" variant="text" @click="day.second_end_picker_open = false">Done</v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-menu>
                  </div>
                  <div class="day-actions">
                    <v-checkbox
                      :model-value="day.is_day_off"
                      label="Day Off"
                      density="compact"
                      hide-details
                      color="success"
                      @update:model-value="setDayOff(day, $event)"
                    />
                  </div>
                  <div class="day-tools">
                    <v-btn prepend-icon="mdi-content-copy" size="x-small" variant="text" :disabled="!canCopyHours(day)" @click="copyDayHours(person, day)">Copy</v-btn>
                    <v-btn prepend-icon="mdi-content-paste" size="x-small" variant="text" :disabled="!canPasteHours(person)" @click="pasteDayHours(person, day)">Paste</v-btn>
                    <v-btn
                      prepend-icon="mdi-call-split"
                      size="x-small"
                      :color="day.is_split_shift ? 'success' : undefined"
                      :variant="day.is_split_shift ? 'tonal' : 'text'"
                      :disabled="day.is_day_off"
                      @click="toggleSplitShift(day)"
                    >Split</v-btn>
                  </div>
                  <v-textarea
                    v-model="day.activities"
                    label="Activities / Observations"
                    rows="3"
                    no-resize
                    density="compact"
                    variant="outlined"
                    hide-details
                    maxlength="10000"
                    @update:model-value="queueAutoSave()"
                    @blur="queueAutoSave(0)"
                  />
                </div>
                <button v-else class="day-summary" type="button" @click="openDayDetails(person, day)">
                  <strong :class="{ 'day-off': day.is_day_off }">{{ dayHoursLabel(day) }}</strong>
                  <span v-if="day.activities">{{ day.activities }}</span>
                  <small v-else>Activities / Obs</small>
                </button>
              </td>
              <td class="weekly-total">{{ hoursLabel(personWeeklyHours(person)) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-card>

    <section v-if="!loading" class="next-week-preview">
      <div class="next-week-heading">
        <small>NEXT WEEK · VIEW ONLY</small>
        <strong>{{ nextWeekHeading }}</strong>
      </div>

      <v-card v-if="nextWeekLoadError || !hasScheduleData(nextPeople)" class="next-week-empty" elevation="0">
        <v-icon size="28" color="primary">mdi-calendar-blank-outline</v-icon>
        <span>{{ nextWeekLoadError ? 'Unable to load the following week.' : 'No Admin Schedule has been added for next week.' }}</span>
      </v-card>

      <v-card v-else class="schedule-panel" elevation="0">
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th class="person-column">Team</th>
                <th v-for="day in nextWeekDays" :key="`next-${day.date}`">
                  <span>{{ day.name }}</span>
                  <strong>{{ day.number }}</strong>
                </th>
                <th class="total-column">Hours</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="person in nextPeople" :key="`next-${person.key}`">
                <td class="person-cell">
                  <img
                    v-if="profilePhoto(person.key)"
                    :src="profilePhoto(person.key)"
                    :alt="`${person.name} profile photo`"
                    class="person-avatar person-avatar-photo"
                  />
                  <div v-else class="person-avatar">{{ initials(person.name) }}</div>
                  <strong>{{ person.name }}</strong>
                  <small>{{ person.role }}</small>
                </td>
                <td v-for="day in person.days" :key="`next-${person.key}-${day.date}`" class="schedule-day-cell">
                  <button class="day-summary" type="button" @click="openDayDetails(person, day)">
                    <strong :class="{ 'day-off': day.is_day_off }">{{ dayHoursLabel(day) }}</strong>
                    <span v-if="day.activities">{{ day.activities }}</span>
                    <small v-else>Activities / Obs</small>
                  </button>
                </td>
                <td class="weekly-total">{{ hoursLabel(personWeeklyHours(person)) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>
    </section>

    <v-dialog v-model="detailsOpen" max-width="600">
      <v-card v-if="selectedDetail">
        <v-card-title>{{ selectedDetail.person.name }}</v-card-title>
        <v-card-subtitle>{{ detailDateLabel(selectedDetail.day.date) }} · {{ dayHoursLabel(selectedDetail.day) }}</v-card-subtitle>
        <v-card-text class="detail-activities">
          {{ selectedDetail.day.activities || 'No activities or observations were added.' }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="detailsOpen = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="2600">{{ snackbar.message }}</v-snackbar>
  </main>
</template>

<script>
import { addDays, addWeeks, format, parseISO, startOfWeek, subWeeks } from 'date-fns';
import * as XLSX from 'xlsx-js-style';
import secureAxios from '@/utils/secureAxios';
import gheraldineProfilePhoto from '@/assets/admin-schedule/gheraldine-quintero-profile.png';
import lorenaProfilePhoto from '@/assets/admin-schedule/lorena-hernandez-profile.jpeg';
import dianaProfilePhoto from '@/assets/admin-schedule/diana-bolanos-profile.jpeg';

const PROFILE_PHOTOS = {
  'gheraldine-quintero': gheraldineProfilePhoto,
  'lorena-hernandez': lorenaProfilePhoto,
  'diana-bolanos': dianaProfilePhoto,
};

export default {
  name: 'AdminSchedule',
  data() {
    return {
      weekStart: '',
      people: [],
      nextPeople: [],
      nextWeekLoadError: false,
      loading: false,
      canEdit: false,
      isEditing: false,
      error: '',
      saveState: 'idle',
      autoSaveTimer: null,
      pendingAutoSave: null,
      editRevision: 0,
      loadRequestId: 0,
      detailsOpen: false,
      selectedDetail: null,
      copiedHours: null,
      snackbar: { show: false, message: '', color: 'success' },
    };
  },
  computed: {
    weekEnd() { return format(addDays(parseISO(this.weekStart), 6), 'yyyy-MM-dd'); },
    weekDays() {
      if (!this.weekStart) return [];
      return Array.from({ length: 7 }, (_, index) => {
        const date = addDays(parseISO(this.weekStart), index);
        return { date: format(date, 'yyyy-MM-dd'), name: format(date, 'EEE').toUpperCase(), number: format(date, 'd') };
      });
    },
    weekHeading() {
      if (!this.weekStart) return '';
      const start = parseISO(this.weekStart);
      const end = parseISO(this.weekEnd);
      if (format(start, 'yyyy-MM') === format(end, 'yyyy-MM')) {
        return `${format(start, 'MMMM d')} – ${format(end, 'd, yyyy')}`.toUpperCase();
      }
      return `${format(start, 'MMMM d')} – ${format(end, 'MMMM d, yyyy')}`.toUpperCase();
    },
    nextWeekStart() {
      return this.weekStart ? format(addWeeks(parseISO(this.weekStart), 1), 'yyyy-MM-dd') : '';
    },
    nextWeekEnd() {
      return this.nextWeekStart ? format(addDays(parseISO(this.nextWeekStart), 6), 'yyyy-MM-dd') : '';
    },
    nextWeekDays() {
      if (!this.weekStart) return [];
      return Array.from({ length: 7 }, (_, index) => {
        const date = addDays(parseISO(this.nextWeekStart), index);
        return { date: format(date, 'yyyy-MM-dd'), name: format(date, 'EEE').toUpperCase(), number: format(date, 'd') };
      });
    },
    nextWeekHeading() {
      if (!this.weekStart) return '';
      const start = parseISO(this.nextWeekStart);
      const end = parseISO(this.nextWeekEnd);
      if (format(start, 'yyyy-MM') === format(end, 'yyyy-MM')) {
        return `${format(start, 'MMMM d')} – ${format(end, 'd, yyyy')}`.toUpperCase();
      }
      return `${format(start, 'MMMM d')} – ${format(end, 'MMMM d, yyyy')}`.toUpperCase();
    },
    saveStatusText() {
      return { idle: 'Auto-save on', unsaved: 'Waiting to save…', saving: 'Saving…', saved: 'Saved', error: 'Save failed' }[this.saveState] || 'Auto-save on';
    },
  },
  watch: {
    weekStart() { if (this.weekStart) this.loadWeek(); },
  },
  created() {
    this.weekStart = format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
  },
  beforeUnmount() {
    clearTimeout(this.autoSaveTimer);
    if (this.pendingAutoSave) this.saveWeek(this.pendingAutoSave);
  },
  methods: {
    moveWeek(direction) { this.weekStart = format(addWeeks(parseISO(this.weekStart), direction), 'yyyy-MM-dd'); },
    goToCurrentWeek() { this.weekStart = format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'); },
    async loadWeek() {
      const requestId = ++this.loadRequestId;
      this.loading = true;
      this.error = '';
      this.nextWeekLoadError = false;
      this.isEditing = false;
      this.copiedHours = null;
      try {
        const [currentResponse, nextResponse] = await Promise.all([
          secureAxios.get('/api/admin-schedule/week', { params: { week_start: this.weekStart } }),
          secureAxios.get('/api/admin-schedule/week', { params: { week_start: this.nextWeekStart } })
            .catch((error) => {
              console.error('Unable to load the next Admin Schedule:', error?.message || error);
              return null;
            }),
        ]);
        if (requestId !== this.loadRequestId) return;
        const data = currentResponse.data || {};
        this.people = this.preparePeople(data.people || []);
        this.nextPeople = this.preparePeople(nextResponse?.data?.people || []);
        this.nextWeekLoadError = !nextResponse;
        this.canEdit = data.can_edit === true;
        this.saveState = 'idle';
      } catch (error) {
        if (requestId !== this.loadRequestId) return;
        this.people = [];
        this.nextPeople = [];
        this.error = error.response?.data?.message || 'Unable to load Admin Schedule.';
      } finally {
        if (requestId === this.loadRequestId) this.loading = false;
      }
    },
    preparePeople(people) {
      return people.map((person) => ({
          ...person,
          days: (person.days || []).map((day) => ({
            ...day,
            start_picker_open: false,
            end_picker_open: false,
            second_start_picker_open: false,
            second_end_picker_open: false,
          })),
        }));
    },
    savePayload() {
      return {
        weekStart: this.weekStart,
        revision: this.editRevision,
        entries: this.people.flatMap((person) => person.days.map((day) => ({
          person_key: person.key,
          work_date: day.date,
          start_time: day.start_time || null,
          end_time: day.end_time || null,
          second_start_time: day.second_start_time || null,
          second_end_time: day.second_end_time || null,
          is_split_shift: !!day.is_split_shift,
          is_day_off: !!day.is_day_off,
          activities: day.activities || '',
        }))),
      };
    },
    queueAutoSave(delay = 700) {
      if (!this.canEdit || this.loading || !this.people.length) return;
      this.editRevision += 1;
      this.saveState = 'unsaved';
      this.error = '';
      this.pendingAutoSave = this.savePayload();
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = setTimeout(() => {
        const payload = this.pendingAutoSave;
        this.pendingAutoSave = null;
        this.saveWeek(payload);
      }, delay);
    },
    async saveWeek(payload) {
      if (!payload?.weekStart) return;
      if (payload.revision === this.editRevision) this.saveState = 'saving';
      try {
        await secureAxios.put('/api/admin-schedule/week', { week_start: payload.weekStart, entries: payload.entries });
        if (payload.revision === this.editRevision) this.saveState = 'saved';
      } catch (error) {
        if (payload.revision === this.editRevision) {
          this.saveState = 'error';
          this.error = error.response?.data?.message || 'Unable to auto-save Admin Schedule.';
        }
      }
    },
    setDayOff(day, value) {
      day.is_day_off = !!value;
      if (day.is_day_off) {
        day.start_picker_open = false;
        day.end_picker_open = false;
        day.second_start_picker_open = false;
        day.second_end_picker_open = false;
        day.start_time = '';
        day.end_time = '';
        day.second_start_time = '';
        day.second_end_time = '';
        day.is_split_shift = false;
      }
      this.queueAutoSave(0);
    },
    toggleSplitShift(day) {
      day.is_split_shift = !day.is_split_shift;
      if (!day.is_split_shift) {
        day.second_start_picker_open = false;
        day.second_end_picker_open = false;
        day.second_start_time = '';
        day.second_end_time = '';
      }
      this.queueAutoSave(0);
    },
    canCopyHours(day) {
      return !day.is_day_off && !!(day.start_time || day.end_time || day.second_start_time || day.second_end_time);
    },
    canPasteHours(person) {
      return !!this.copiedHours && this.copiedHours.person_key === person.key;
    },
    copyDayHours(person, day) {
      if (!this.canCopyHours(day)) return;
      this.copiedHours = {
        person_key: person.key,
        start_time: day.start_time || '',
        end_time: day.end_time || '',
        second_start_time: day.second_start_time || '',
        second_end_time: day.second_end_time || '',
        is_split_shift: !!day.is_split_shift,
      };
      this.notify(`Hours copied for ${person.name}`);
    },
    pasteDayHours(person, day) {
      if (!this.canPasteHours(person)) return;
      day.is_day_off = false;
      day.start_time = this.copiedHours.start_time;
      day.end_time = this.copiedHours.end_time;
      day.second_start_time = this.copiedHours.second_start_time;
      day.second_end_time = this.copiedHours.second_end_time;
      day.is_split_shift = this.copiedHours.is_split_shift;
      this.queueAutoSave(0);
      this.notify(`Hours pasted to ${this.detailDateLabel(day.date)}`);
    },
    clearTime(day, field, menuField) {
      day[field] = '';
      day[menuField] = false;
      this.queueAutoSave(0);
    },
    async copyPreviousWeek() {
      if (this.hasScheduleData(this.people) && !window.confirm('Replace this week with the previous week’s schedule?')) return;
      try {
        const previousStart = format(subWeeks(parseISO(this.weekStart), 1), 'yyyy-MM-dd');
        const { data } = await secureAxios.get('/api/admin-schedule/week', { params: { week_start: previousStart } });
        if (!this.hasScheduleData(data.people || [])) return this.notify('The previous week is empty.', 'warning');
        const previousByPerson = new Map((data.people || []).map((person) => [person.key, person]));
        this.people.forEach((person) => {
          const previous = previousByPerson.get(person.key);
          if (!previous) return;
          person.days.forEach((day, index) => {
            const source = previous.days[index] || {};
            day.start_time = source.start_time || '';
            day.end_time = source.end_time || '';
            day.second_start_time = source.second_start_time || '';
            day.second_end_time = source.second_end_time || '';
            day.is_split_shift = !!source.is_split_shift;
            day.is_day_off = !!source.is_day_off;
            day.activities = source.activities || '';
          });
        });
        this.queueAutoSave(0);
        this.notify('Previous week copied');
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to copy the previous week.', 'error');
      }
    },
    hasScheduleData(people) {
      return people.some((person) => person.days.some((day) => day.start_time || day.end_time || day.second_start_time || day.second_end_time || day.is_split_shift || day.is_day_off || day.activities));
    },
    openDayDetails(person, day) {
      this.selectedDetail = { person, day };
      this.detailsOpen = true;
    },
    dayHoursLabel(day) {
      if (day.is_day_off) return 'DAY OFF';
      const first = this.timeRangeLabel(day.start_time, day.end_time);
      const second = day.is_split_shift ? this.timeRangeLabel(day.second_start_time, day.second_end_time) : '';
      return [first, second].filter(Boolean).join('\n') || '—';
    },
    timeRangeLabel(startTime, endTime) {
      if (startTime && endTime) return `${this.formatTime(startTime)} – ${this.formatTime(endTime)}`;
      if (startTime) return `Starts ${this.formatTime(startTime)}`;
      if (endTime) return `Until ${this.formatTime(endTime)}`;
      return '';
    },
    formatTime(value) {
      const [hourValue, minute = '00'] = String(value || '').split(':');
      const hour = Number(hourValue);
      if (!Number.isFinite(hour)) return '';
      return `${hour % 12 || 12}:${minute} ${hour >= 12 ? 'PM' : 'AM'}`;
    },
    dayHours(day) {
      if (day.is_day_off) return 0;
      return this.timeRangeHours(day.start_time, day.end_time)
        + (day.is_split_shift ? this.timeRangeHours(day.second_start_time, day.second_end_time) : 0);
    },
    timeRangeHours(startTime, endTime) {
      if (!startTime || !endTime) return 0;
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);
      let minutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
      if (minutes < 0) minutes += 24 * 60;
      return minutes / 60;
    },
    personWeeklyHours(person) {
      return person.days
        .filter((day) => !day.is_day_off)
        .reduce((total, day) => total + this.dayHours(day), 0);
    },
    hoursLabel(value) { return Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 2 }); },
    initials(name) { return String(name || '').split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase(); },
    profilePhoto(personKey) { return PROFILE_PHOTOS[personKey] || ''; },
    detailDateLabel(date) { return format(parseISO(date), 'EEEE, MMMM d, yyyy'); },
    downloadExcel() {
      const header = ['Team', ...this.weekDays.map((day) => `${day.name} ${day.number}`), 'Total Hours'];
      const rows = this.people.map((person) => [
        `${person.name}\n${person.role}`,
        ...person.days.map((day) => {
          const activities = String(day.activities || '').trim();
          return `${this.dayHoursLabel(day)}${activities ? `\n\n${activities}` : ''}`;
        }),
        this.personWeeklyHours(person),
      ]);
      const data = [[`ADMIN SCHEDULE · ${this.weekHeading}`], header, ...rows];
      const sheet = XLSX.utils.aoa_to_sheet(data);
      sheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 8 } }];
      sheet['!cols'] = [{ wch: 28 }, ...Array.from({ length: 7 }, () => ({ wch: 25 })), { wch: 12 }];
      sheet['!rows'] = [{ hpt: 28 }, { hpt: 24 }, ...this.people.map(() => ({ hpt: 105 }))];
      const range = XLSX.utils.decode_range(sheet['!ref']);
      for (let row = range.s.r; row <= range.e.r; row += 1) {
        for (let col = range.s.c; col <= range.e.c; col += 1) {
          const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col })];
          if (!cell) continue;
          cell.s = {
            alignment: { vertical: 'top', horizontal: row <= 1 ? 'center' : 'left', wrapText: true },
            border: { top: { style: 'thin', color: { rgb: 'D7E0E8' } }, bottom: { style: 'thin', color: { rgb: 'D7E0E8' } }, left: { style: 'thin', color: { rgb: 'D7E0E8' } }, right: { style: 'thin', color: { rgb: 'D7E0E8' } } },
            font: { name: 'Arial', sz: row === 0 ? 16 : 10, bold: row <= 1 || col === 0 },
            fill: { fgColor: { rgb: row === 0 ? '1F4E78' : row === 1 ? '5B9BD5' : row % 2 ? 'F0F4F8' : 'FFFFFF' } },
          };
          if (row <= 1) cell.s.font.color = { rgb: 'FFFFFF' };
        }
      }
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, 'Admin Schedule');
      XLSX.writeFile(workbook, `Admin_Schedule_${this.weekStart}_to_${this.weekEnd}.xlsx`, { cellStyles: true });
      this.notify('Admin Schedule downloaded');
    },
    notify(message, color = 'success') { this.snackbar = { show: true, message, color }; },
  },
};
</script>

<style scoped>
.admin-schedule-page{max-width:1640px;margin:0 auto;padding:22px 22px 70px;color:#203247}.page-heading,.page-actions,.week-control,.control-actions{display:flex;align-items:center}.page-heading{justify-content:space-between;align-items:flex-end;gap:20px;margin:4px 2px 18px}.page-actions{justify-content:flex-end;gap:9px;flex-wrap:wrap}.eyebrow{color:#2f7db9;font-size:11px;font-weight:800;letter-spacing:.14em}.page-heading h1{margin:3px 0 4px;font-size:28px;line-height:1;letter-spacing:-.6px}.page-heading p{margin:0;color:#64768a;font-size:13px}.save-status{color:#7b8792;font-size:12px;font-weight:700;white-space:nowrap}.save-status.unsaved,.save-status.saving{color:#a36d19}.save-status.saved{color:#52835b}.save-status.error{color:#c62828}.schedule-controls{display:grid;grid-template-columns:1fr minmax(430px,1.4fr) 1fr;align-items:center;gap:10px;padding:11px 13px;margin-bottom:16px;border:1px solid #d8d8d8;border-radius:6px}.week-control{justify-content:center;gap:8px}.week-title{min-width:340px;text-align:center}.week-title small{display:block;color:#718095;font-size:10px;font-weight:800;letter-spacing:.08em}.week-title strong{display:block;font-size:25px;font-weight:850;line-height:1.2}.control-actions{justify-content:flex-end;gap:3px}.schedule-panel{border:1px solid #d5dce4;border-radius:7px;overflow:hidden}.table-wrap{overflow-x:auto}table{width:100%;min-width:1380px;border-collapse:collapse;table-layout:fixed}th{height:47px;padding:7px 8px;background:#4f91cd;color:#fff;border-right:1px solid rgba(255,255,255,.3);text-align:center}th span,th strong{display:block}th span{font-size:10px;letter-spacing:.06em}th strong{font-size:16px}.person-column{width:190px}.total-column{width:80px}td{height:145px;padding:0;border-right:1px solid #dce3e9;border-bottom:1px solid #dce3e9;vertical-align:top}tbody tr:nth-child(even){background:#f0f4f8}tbody tr:last-child td{border-bottom:0}.person-cell{padding:15px 12px;text-align:center}.person-avatar{display:flex;width:84px;height:84px;align-items:center;justify-content:center;margin:0 auto 8px;border-radius:50%;background:#e6f0f8;color:#2f6f9f;font-size:14px;font-weight:850}.person-cell strong,.person-cell small{display:block}.person-cell strong{font-size:13px}.person-cell small{margin-top:3px;color:#65778a;font-size:10px;font-weight:750}.schedule-day-cell{position:relative}.day-editor{display:flex;min-height:205px;flex-direction:column;gap:7px;padding:8px}.time-fields{display:grid;grid-template-columns:1fr 1fr;gap:5px}.second-time-fields{padding-top:6px;border-top:1px dashed #b9c9d8}.time-fields :deep(.v-field__input){min-width:0;padding-left:7px;padding-right:4px;font-size:11px}.day-actions{display:flex;min-height:29px;align-items:center;justify-content:space-between;gap:2px}.day-actions :deep(.v-checkbox){flex:1;min-width:0}.day-tools{display:flex;align-items:center;gap:0}.day-tools :deep(.v-btn){width:25px;height:25px}.day-editor :deep(.v-checkbox .v-selection-control){min-height:26px}.day-editor :deep(.v-label){font-size:11px}.day-editor :deep(textarea){font-size:11px;line-height:1.3}.day-summary{display:flex;width:100%;min-height:145px;flex-direction:column;align-items:center;padding:14px 9px;border:0;background:transparent;color:#263746;text-align:center;cursor:pointer}.day-summary:hover{background:rgba(79,145,205,.07)}.day-summary strong{min-height:19px;color:#1264a3;font-size:11px;line-height:1.45;white-space:pre-line}.day-summary strong.day-off{color:#2e7d32}.day-summary span{display:-webkit-box;margin-top:11px;overflow:hidden;font-size:10px;font-weight:650;line-height:1.3;text-align:left;-webkit-box-orient:vertical;-webkit-line-clamp:6}.day-summary small{margin-top:12px;color:#adb6bf;font-size:9px}.weekly-total{padding:17px 8px;color:#214e73;font-size:15px;font-weight:850;text-align:center}.loading-state{display:flex;min-height:300px;flex-direction:column;align-items:center;justify-content:center;gap:10px;color:#64768a}.detail-activities{min-height:100px;color:#2c3945;line-height:1.55;white-space:pre-wrap}@media(max-width:1050px){.page-heading{align-items:flex-start;flex-direction:column}.schedule-controls{grid-template-columns:1fr}.week-control{justify-content:flex-start}.control-actions{justify-content:flex-start}}@media(max-width:650px){.admin-schedule-page{padding:18px 10px 55px}.page-actions{justify-content:flex-start}.week-title{min-width:220px}.week-title strong{font-size:20px}}
.time-fields :deep(.v-field__input){padding-right:7px}
.day-actions{justify-content:flex-start}
.day-tools{display:grid;grid-template-columns:repeat(3,1fr);gap:2px}
.day-tools :deep(.v-btn){width:auto;min-width:0;height:25px;padding:0 3px;font-size:9px}
.day-tools :deep(.v-btn__prepend){margin-inline-end:2px}
.time-fields :deep(input[type="time"]){appearance:none;-webkit-appearance:none}
.time-fields :deep(input[type="time"]::-webkit-calendar-picker-indicator){display:none}
.time-picker-card{overflow:hidden}
.person-avatar-photo{display:block;object-fit:cover}
.next-week-preview{margin-top:32px;padding-top:24px;border-top:1px solid #cfd9e2}.next-week-heading{margin-bottom:14px;text-align:center}.next-week-heading small{display:block;color:#718095;font-size:10px;font-weight:850;letter-spacing:.11em}.next-week-heading strong{display:block;margin-top:3px;color:#24496b;font-size:22px;font-weight:850}.next-week-empty{display:flex;min-height:145px;align-items:center;justify-content:center;gap:10px;border:1px solid #d8e0e7;color:#687a8b;font-size:13px;font-weight:700}
</style>
